import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Snackbar, Alert, MenuItem, Paper, Divider,
  Switch as MuiSwitch,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import AdminPageHeader from "../../components/admin/AdminPageHeader";
import AdminGridToolbar from "../../components/admin/AdminGridToolbar";
import AdminFilterPanel, { filterFieldSx } from "../../components/admin/AdminFilterPanel";
import AdminFormDialog from "../../components/admin/AdminFormDialog";
import { listCertificates, saveCertificate, deleteCertificate, listCompanies } from "../../services/adminService";

const GRID_SX = {
  border: 0,
  "& .MuiDataGrid-columnHeaders": { backgroundColor: "rgba(0,0,0,0.03)", borderBottom: "1px solid rgba(0,0,0,0.12)" },
  "& .MuiDataGrid-row":           { borderBottom: "1px solid rgba(0,0,0,0.06)" },
  "& .MuiDataGrid-cell":          { outline: "none" },
};

const EMPTY = { cnpj: "", certificate: "", passphrase: "", tempo_job: "" };

export default function CertificadosPage() {
  const { t } = useTranslation();
  const [rows, setRows]               = useState([]);
  const [companies, setCompanies]     = useState([]);
  const [search, setSearch]           = useState("");
  const [loading, setLoading]         = useState(false);
  const [rowSelectionModel, setRowSel]= useState({ type: "include", ids: new Set() });
  const [dialogOpen, setDialogOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen]   = useState(false);
  const [editing, setEditing]         = useState(null);
  const [form, setForm]               = useState(EMPTY);
  const [saving, setSaving]           = useState(false);
  const [snack, setSnack]             = useState({ open: false, msg: "", severity: "success" });

  const selectedIds = useMemo(() => Array.from(rowSelectionModel.ids), [rowSelectionModel]);
  const selectedRow = useMemo(() => rows.find(r => r.cnpj === selectedIds[0]), [rows, selectedIds]);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await listCertificates()); }
    catch (e) { showSnack(e.message, "error"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { listCompanies().then(setCompanies).catch(() => {}); load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(r =>
      r.cnpj?.toLowerCase().includes(q) ||
      r.company?.company_name?.toLowerCase().includes(q)
    );
  }, [rows, search]);

  const showSnack   = (msg, severity = "success") => setSnack({ open: true, msg, severity });
  const openNew     = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit    = () => {
    if (!selectedRow) return;
    setEditing(selectedRow);
    setForm({ cnpj: selectedRow.cnpj, certificate: selectedRow.certificate || "", passphrase: selectedRow.passphrase || "", tempo_job: selectedRow.tempo_job ?? "" });
    setDialogOpen(true);
  };
  const closeDialog = () => setDialogOpen(false);

  const handleSave = async () => {
    if (!form.cnpj) return showSnack(t("admin.required.empresa"), "error");
    setSaving(true);
    try {
      await saveCertificate({ ...form, passphrase: form.passphrase || null, tempo_job: form.tempo_job !== "" ? Number(form.tempo_job) : null });
      showSnack(editing ? t("admin.success.certUpdated") : t("admin.success.certSaved"));
      closeDialog();
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    setSaving(true);
    try {
      await Promise.all(selectedIds.map(id => deleteCertificate(id)));
      showSnack(t("admin.success.certDeleted"));
      setDeleteOpen(false);
      setRowSel({ type: "include", ids: new Set() });
      load();
    } catch (e) { showSnack(e.message, "error"); }
    finally { setSaving(false); }
  };

  const columns = [
    { field: "cnpj",        headerName: t("admin.col.cnpj"),        width: 160 },
    { field: "company",     headerName: t("admin.col.empresa"),      flex: 1, valueGetter: (_, row) => row.company?.company_name || "" },
    { field: "tempo_job",   headerName: t("admin.col.tempoJob"),     width: 130 },
    { field: "last_nsu",    headerName: t("admin.col.lastNsu"),      width: 150, valueGetter: (_, row) => row.company?.last_nsu || "—" },
    {
      field: "certificate", headerName: t("admin.col.certificado"), width: 120,
      renderCell: ({ value }) => (
        <MuiSwitch checked={!!value} size="small" disableRipple sx={{ pointerEvents: "none" }} />
      ),
    },
  ];

  const f = (field) => ({ value: form[field] ?? "", onChange: e => setForm(p => ({ ...p, [field]: e.target.value })) });

  return (
    <Box sx={{ p: 2 }}>
      <AdminPageHeader title={t("admin.pages.certificados")} />

      <AdminFilterPanel>
        <TextField label="CNPJ / Empresa" size="small"
          value={search} onChange={e => setSearch(e.target.value)}
          InputLabelProps={{ shrink: true }} sx={{ ...filterFieldSx, width: 280 }} />
      </AdminFilterPanel>

      <Paper variant="outlined">
        <Divider />
        <DataGrid
          rows={filtered} columns={columns} getRowId={r => r.cnpj}
          loading={loading} autoHeight checkboxSelection disableRowSelectionOnClick
          density="compact" showToolbar
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={setRowSel}
          slots={{ toolbar: AdminGridToolbar }}
          slotProps={{ toolbar: {
            title: t("admin.grid.certificados"),
            onNew: openNew, onEdit: openEdit, onDelete: () => setDeleteOpen(true),
            canEdit: selectedIds.length === 1, canDelete: selectedIds.length > 0,
          }}}
          sx={GRID_SX}
        />
      </Paper>

      <AdminFormDialog
        open={dialogOpen} onClose={closeDialog} onSave={handleSave} saving={saving}
        title={editing ? t("admin.dialog.editCertificado") : t("admin.dialog.newCertificado")}
      >
        <TextField select label="Empresa *" {...f("cnpj")} disabled={!!editing} size="small"
          InputLabelProps={{ shrink: true }}>
          {companies.map(c => <MenuItem key={c.cnpj} value={c.cnpj}>{c.cnpj} — {c.company_name}</MenuItem>)}
        </TextField>
        <TextField label="Certificado (Base64 PFX)" {...f("certificate")} multiline rows={5}
          InputLabelProps={{ shrink: true }}
          inputProps={{ style: { fontFamily: "monospace", fontSize: 11 } }}
          helperText={t("admin.helper.certBase64")} />
        <TextField label="Senha do Certificado" type="password" {...f("passphrase")} size="small"
          InputLabelProps={{ shrink: true }}
          helperText={t("admin.helper.certPassword")} />
        <TextField label="Tempo do Job (segundos)" type="number" {...f("tempo_job")} size="small"
          InputLabelProps={{ shrink: true }} />
      </AdminFormDialog>

      <Dialog open={deleteOpen} onClose={() => !saving && setDeleteOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: 14, pb: 1, borderBottom: "1px solid rgba(0,0,0,0.08)" }}>{t("admin.dialog.confirmDelete")}</DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 1 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary", lineHeight: 1.6 }}>
            {t("admin.confirm.deleteCertificado", { count: selectedIds.length })}
          </Typography>
        </DialogContent>
        <Divider sx={{ opacity: 0.5 }} />
        <DialogActions sx={{ px: 2, py: 1.2, gap: 1 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={saving} sx={{ textTransform: "none", fontSize: 13 }}>{t("admin.buttons.cancel")}</Button>
          <Button onClick={handleDelete} disabled={saving} disableElevation sx={{ textTransform: "none", fontSize: 13, color: "#d32f2f", backgroundColor: "rgba(211,47,47,0.10)", "&:hover": { backgroundColor: "rgba(211,47,47,0.18)" }, borderRadius: 0 }}>
            {saving ? t("admin.buttons.deleting") : t("admin.buttons.delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(p => ({ ...p, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(p => ({ ...p, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
